import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, spring, interpolate } from 'remotion';

/**
 * #146 SplitText 文字动画 (GSAP SplitText 风格)
 * 将文字拆分成字母，然后分别动画
 */
export const SplitTextAnimation: React.FC<{
  text: string;
  color: string;
  backgroundColor: string;
  fontSize?: number;
  stagger?: number;
}> = ({ text, color, backgroundColor, fontSize = 120, stagger = 3 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const letters = text.split('');
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'bold',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        {letters.map((letter, index) => {
          const delay = index * stagger;
          const letterProgress = spring({
            frame: frame - delay,
            fps,
            config: {
              damping: 12,
              stiffness: 100,
              mass: 0.5,
            },
          });
          
          const y = interpolate(letterProgress, [0, 1], [100, 0]);
          const opacity = interpolate(letterProgress, [0, 1], [0, 1]);
          const rotateX = interpolate(letterProgress, [0, 1], [90, 0]);
          const scale = interpolate(letterProgress, [0, 1], [0.5, 1]);
          
          return (
            <div
              key={index}
              style={{
                fontSize,
                color,
                display: 'inline-block',
                transform: `translateY(${y}px) rotateX(${rotateX}deg) scale(${scale})`,
                opacity,
                textShadow: `0 0 30px ${color}`,
                margin: '0 5px',
              }}
            >
              {letter === ' ' ? '\u00A0' : letter}
            </div>
          );
        })}
      </div>
      
      {/* 装饰线条 */}
      <div
        style={{
          position: 'absolute',
          bottom: '30%',
          width: `${interpolate(spring({ frame, fps, config: { damping: 200 } }), [0, 1], [0, 60])}%`,
          height: 3,
          backgroundColor: color,
          opacity: 0.5,
        }}
      />
    </AbsoluteFill>
  );
};

/**
 * #147 磁性元素效果
 * 元素被"磁性"吸引效果
 */
export const MagneticElement: React.FC<{
  text: string;
  color: string;
  backgroundColor: string;
  magneticStrength?: number;
}> = ({ text, color, backgroundColor, magneticStrength = 50 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  // 模拟鼠标位置（圆形轨迹）
  const mouseAngle = frame * 0.05;
  const mouseRadius = 200;
  const mouseX = Math.cos(mouseAngle) * mouseRadius;
  const mouseY = Math.sin(mouseAngle) * mouseRadius;
  
  // 磁性吸引力（Spring 动画）
  const magnetProgress = spring({
    frame,
    fps,
    config: {
      damping: 15,
      stiffness: 80,
    },
  });
  
  const elementX = mouseX * magnetProgress * 0.3;
  const elementY = mouseY * magnetProgress * 0.3;
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'bold',
      }}
    >
      {/* 中心磁性元素 */}
      <div
        style={{
          fontSize: 100,
          color,
          transform: `translate(${elementX}px, ${elementY}px)`,
          textShadow: `0 0 40px ${color}`,
          transition: 'transform 0.1s ease-out',
          zIndex: 10,
        }}
      >
        {text}
      </div>
      
      {/* 模拟鼠标光标 */}
      <div
        style={{
          position: 'absolute',
          left: `calc(50% + ${mouseX}px)`,
          top: `calc(50% + ${mouseY}px)`,
          width: 20,
          height: 20,
          borderRadius: '50%',
          backgroundColor: color,
          opacity: 0.5,
          transform: 'translate(-50%, -50%)',
          boxShadow: `0 0 30px ${color}`,
        }}
      />
      
      {/* 磁场线 */}
      <svg
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          opacity: 0.2,
        }}
      >
        {[...Array(5)].map((_, i) => {
          const radius = 100 + i * 50;
          const opacity = 1 - i * 0.2;
          return (
            <circle
              key={i}
              cx="50%"
              cy="50%"
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth={2}
              strokeDasharray="10,10"
              opacity={opacity}
            />
          );
        })}
      </svg>
      
      {/* 提示文字 */}
      <div
        style={{
          position: 'absolute',
          bottom: 100,
          fontSize: 32,
          color,
          opacity: 0.6,
        }}
      >
        Magnetic Effect
      </div>
    </AbsoluteFill>
  );
};
